import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { TaskBoardCard } from '../components/TaskBoardCard';
import { WorkflowColumnManager } from '../components/WorkflowColumnManager';
import * as taskboardService from '../services/taskboardService';
import * as teamService from '../services/teamService';
import { getUserPreferences, updateUserPreferences } from '../services/userService';

export function TaskBoard(): React.ReactElement {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'company_admin' || user?.role === 'platform_admin';

  // State
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [sprints, setSprints] = useState<any[]>([]);
  const [selectedSprintId, setSelectedSprintId] = useState<string>('');
  const [boardData, setBoardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | null>(null);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [viewMode, setViewMode] = useState<'stories' | 'subtasks'>('stories');
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  // Dependency blocking modal
  const [blockedModal, setBlockedModal] = useState<{
    visible: boolean;
    requirementId: string;
    targetColumnId: string;
    targetOrder: number;
    canOverride: boolean;
    unmetDependencies: Array<{ id: string; title: string; code: string; status: string }>;
  } | null>(null);
  const [overriding, setOverriding] = useState(false);

  // Team filter
  const isManager = user?.role === 'platform_admin' || user?.role === 'company_admin' ||
                    user?.role === 'project_manager' || user?.role === 'project_manager_lite';
  const [teams, setTeams] = useState<teamService.Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [teamMemberIds, setTeamMemberIds] = useState<Set<string>>(new Set());

  // Close settings dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    }
    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettings]);

  // Extract unique assignees from all cards in the current sprint
  const assignees = useMemo(() => {
    if (!boardData?.columns) return [];

    const assigneeMap = new Map<string, { id: string; name: string; initials: string }>();

    boardData.columns.forEach((col: any) => {
      col.requirements?.forEach((req: any) => {
        if (req.assigned_to && req.assigned_to_name && !assigneeMap.has(req.assigned_to)) {
          const nameParts = req.assigned_to_name.trim().split(' ');
          const initials = nameParts.length >= 2
            ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
            : nameParts[0].substring(0, 2).toUpperCase();

          assigneeMap.set(req.assigned_to, {
            id: req.assigned_to,
            name: req.assigned_to_name,
            initials
          });
        }
      });
    });

    return Array.from(assigneeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [boardData]);

  // Filter columns based on selected assignee, overdue, and team
  const filteredBoardData = useMemo(() => {
    if (!boardData || (!selectedAssigneeId && !overdueOnly && !selectedTeamId)) return boardData;

    const now = new Date();
    return {
      ...boardData,
      columns: boardData.columns.map((col: any) => ({
        ...col,
        requirements: col.requirements?.filter((req: any) => {
          if (selectedTeamId && teamMemberIds.size > 0 && !teamMemberIds.has(req.assigned_to) && !teamMemberIds.has(req.created_by)) return false;
          if (selectedAssigneeId && req.assigned_to !== selectedAssigneeId) return false;
          if (overdueOnly && !(req.due_date && new Date(req.due_date) < now)) return false;
          return true;
        }) || []
      }))
    };
  }, [boardData, selectedAssigneeId, overdueOnly, selectedTeamId, teamMemberIds]);

  // Load projects
  useEffect(() => {
    async function loadProjects() {
      if (!token) return;
      try {
        const res = await taskboardService.getTaskboardProjects(token);
        const projectsList = res.data || [];
        setProjects(projectsList);
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    }
    loadProjects();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load teams and saved team preference
  useEffect(() => {
    async function loadTeams() {
      if (!token || !isManager) return;
      try {
        const [teamsList, prefs] = await Promise.all([
          teamService.listTeams(token),
          getUserPreferences(token)
        ]);
        setTeams(teamsList);
        const savedTeamId = prefs?.selectedTeamId;
        if (savedTeamId && teamsList.some((t: teamService.Team) => t.id === savedTeamId)) {
          setSelectedTeamId(savedTeamId);
          const members = await teamService.getTeamById(savedTeamId, token);
          setTeamMemberIds(new Set(members.members.map((m: teamService.TeamMember) => m.user_id)));
        }
      } catch (err) {
        console.error('Error loading teams:', err);
      }
    }
    loadTeams();
  }, [token, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle team change
  const handleTeamChange = useCallback(async (teamId: string) => {
    if (!token) return;
    setSelectedTeamId(teamId);
    try {
      await updateUserPreferences({ selectedTeamId: teamId || null }, token);
      if (teamId) {
        const teamData = await teamService.getTeamById(teamId, token);
        setTeamMemberIds(new Set(teamData.members.map((m: teamService.TeamMember) => m.user_id)));
      } else {
        setTeamMemberIds(new Set());
      }
    } catch (err) {
      console.error('Error changing team:', err);
    }
  }, [token]);

  // Load sprints when project changes
  useEffect(() => {
    async function loadSprints() {
      if (!token) return;
      try {
        // Load sprints for selected project, or all sprints if "All Projects"
        const res = await taskboardService.getSprints(selectedProjectId || '', token);
        const sprintList = res.data || [];
        setSprints(sprintList);
        // Auto-select active sprint
        const active = sprintList.find((s: any) => s.status === 'active');
        if (active) {
          setSelectedSprintId(active.id);
        } else if (sprintList.length > 0) {
          setSelectedSprintId(sprintList[0].id);
        } else {
          setSelectedSprintId('');
        }
        setViewMode('stories');
      } catch (err) {
        console.error('Error loading sprints:', err);
      }
    }
    loadSprints();
  }, [token, selectedProjectId]);

  // Load board data
  const loadBoardData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await taskboardService.getBoardData(selectedProjectId || '', token, selectedSprintId || undefined, viewMode);
      setBoardData(res.data);
    } catch (err) {
      console.error('Error loading board:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedProjectId, selectedSprintId, viewMode]);

  useEffect(() => {
    loadBoardData();
  }, [loadBoardData]);

  // Handle drag end
  async function handleDragEnd(result: DropResult) {
    if (!result.destination || !token) return;

    const { draggableId, destination } = result;
    const columnId = destination.droppableId;
    const order = destination.index;

    // Optimistic update
    if (boardData) {
      const newColumns = boardData.columns.map((col: any) => ({
        ...col,
        requirements: col.requirements.filter((r: any) => r.id !== draggableId),
      }));
      const targetCol = newColumns.find((c: any) => c.id === columnId);
      if (targetCol) {
        const movedReq = boardData.columns
          .flatMap((c: any) => c.requirements)
          .find((r: any) => r.id === draggableId);
        if (movedReq) {
          targetCol.requirements.splice(order, 0, { ...movedReq, board_column_id: columnId });
        }
      }
      setBoardData({ ...boardData, columns: newColumns });
    }

    try {
      await taskboardService.moveCard(draggableId, columnId, order, token);
    } catch (err: any) {
      const errorData = err.response?.data;
      if (errorData?.error === 'blocked_by_dependencies') {
        loadBoardData(); // Revert optimistic update
        setBlockedModal({
          visible: true,
          requirementId: draggableId,
          targetColumnId: columnId,
          targetOrder: order,
          canOverride: errorData.can_override,
          unmetDependencies: errorData.unmet_dependencies,
        });
        return;
      }
      console.error('Error moving card:', err);
      loadBoardData(); // Revert on error
    }
  }

  async function handleForceOverride() {
    if (!blockedModal || !token) return;
    setOverriding(true);
    try {
      await taskboardService.moveCard(
        blockedModal.requirementId,
        blockedModal.targetColumnId,
        blockedModal.targetOrder,
        token,
        true
      );
      setBlockedModal(null);
      loadBoardData();
    } catch (err) {
      console.error('Error overriding dependency block:', err);
    } finally {
      setOverriding(false);
    }
  }

  function handleRefresh() {
    loadBoardData();
  }

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-6 pb-0">
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Task Board</h1>
                <div className="flex items-center gap-3">
                  {/* Tab toggle */}
                  <div className="flex bg-white/20 rounded-lg p-0.5">
                    <button
                      className="px-3 py-1.5 text-sm font-medium rounded-md bg-white text-indigo-600 shadow-sm transition-colors"
                    >
                      Board
                    </button>
                    <button
                      onClick={() => navigate('/taskboard/backlog' + (selectedProjectId ? `?project_id=${selectedProjectId}` : ''))}
                      className="px-3 py-1.5 text-sm font-medium rounded-md text-blue-100 hover:text-white transition-colors"
                    >
                      Backlog
                    </button>
                  </div>
                  {/* Assignee filter avatars */}
                  {assignees.length > 0 && (
                    <div className="flex items-center gap-1.5 ml-8">
                      {assignees.map(assignee => (
                        <button
                          key={assignee.id}
                          onClick={() => setSelectedAssigneeId(
                            selectedAssigneeId === assignee.id ? null : assignee.id
                          )}
                          title={`${assignee.name}${selectedAssigneeId === assignee.id ? ' (Click to clear filter)' : ''}`}
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                            selectedAssigneeId === assignee.id
                              ? 'bg-white text-indigo-600 ring-2 ring-white ring-offset-2 ring-offset-indigo-600 scale-110'
                              : selectedAssigneeId
                                ? 'bg-white/30 text-white/60 hover:bg-white/50'
                                : 'bg-white/20 text-white hover:bg-white/40'
                          }`}
                        >
                          {assignee.initials}
                        </button>
                      ))}
                      {selectedAssigneeId && (
                        <button
                          onClick={() => setSelectedAssigneeId(null)}
                          title="Clear filter"
                          className="ml-1 w-6 h-6 rounded-full bg-white/20 text-white hover:bg-white/40 flex items-center justify-center transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                  {/* Overdue filter */}
                  <label className="flex items-center gap-2 ml-6 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={overdueOnly}
                      onChange={e => setOverdueOnly(e.target.checked)}
                      className="rounded text-red-500 focus:ring-red-400 border-white/30 bg-white/20"
                    />
                    <span className="text-sm text-white font-medium">Overdue Only</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Team dropdown */}
                {isManager && teams.length > 0 && (
                  <select
                    value={selectedTeamId}
                    onChange={e => handleTeamChange(e.target.value)}
                    className="min-w-[140px] max-w-[200px] pl-3 pr-8 py-2 border border-gray-300 bg-white rounded-md text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none shadow-sm cursor-pointer truncate"
                  >
                    <option value="">All Teams</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                )}

                {/* Project dropdown */}
                <select
                  value={selectedProjectId}
                  onChange={e => setSelectedProjectId(e.target.value)}
                  className="min-w-[180px] max-w-[280px] pl-3 pr-8 py-2 border border-gray-300 bg-white rounded-md text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none shadow-sm cursor-pointer truncate"
                >
                  <option value="">All Projects</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>
                  ))}
                </select>

                {/* Sprint dropdown */}
                {sprints.length > 0 && (
                  <select
                    value={selectedSprintId}
                    onChange={e => setSelectedSprintId(e.target.value)}
                    className="min-w-[160px] max-w-[220px] pl-3 pr-8 py-2 border border-gray-300 bg-white rounded-md text-sm text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none shadow-sm cursor-pointer truncate"
                  >
                    {sprints.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} {s.status === 'active' ? '(Active)' : `(${s.status})`}{s.team_name ? ` [${s.team_name}]` : ''}
                      </option>
                    ))}
                  </select>
                )}

                {/* Settings gear */}
                <div className="relative" ref={settingsRef}>
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="w-9 h-9 flex items-center justify-center text-white border border-white/30 rounded-md hover:bg-white/10 transition-colors"
                    title="Board Settings"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>

                  {showSettings && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50">
                      {/* View mode */}
                      <div className="px-3 py-2">
                        <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">Taskboard Settings</p>
                        <div className="flex bg-indigo-600 rounded-lg p-0.5">
                          <button
                            onClick={() => { setViewMode('stories'); }}
                            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                              viewMode === 'stories'
                                ? 'bg-white text-indigo-600 shadow-sm'
                                : 'text-blue-100 hover:text-white'
                            }`}
                          >
                            Stories
                          </button>
                          <button
                            onClick={() => { setViewMode('subtasks'); }}
                            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                              viewMode === 'subtasks'
                                ? 'bg-white text-indigo-600 shadow-sm'
                                : 'text-blue-100 hover:text-white'
                            }`}
                          >
                            Subtasks
                          </button>
                        </div>
                      </div>

                      {/* Manage Columns (admin only) */}
                      {isAdmin && boardData?.columns && (
                        <>
                          <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                          <button
                            onClick={() => { setShowSettings(false); setShowColumnManager(true); }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                          >
                            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" />
                            </svg>
                            Manage Columns
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Active filter indicators */}
        {(selectedAssigneeId || overdueOnly || selectedTeamId) && (
          <div className="px-6 pt-4 flex items-center gap-3">
            {selectedTeamId && (
              <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded-full text-sm">
                <span>Team:</span>
                <span className="font-semibold">
                  {teams.find(t => t.id === selectedTeamId)?.name}
                </span>
                <button
                  onClick={() => handleTeamChange('')}
                  className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {selectedAssigneeId && (
              <div className="inline-flex items-center gap-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-3 py-1.5 rounded-full text-sm">
                <span>Showing cards assigned to:</span>
                <span className="font-semibold">
                  {assignees.find(a => a.id === selectedAssigneeId)?.name}
                </span>
                <button
                  onClick={() => setSelectedAssigneeId(null)}
                  className="ml-1 hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {overdueOnly && (
              <div className="inline-flex items-center gap-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1.5 rounded-full text-sm">
                <span className="font-semibold">Showing overdue only</span>
                <button
                  onClick={() => setOverdueOnly(false)}
                  className="ml-1 hover:bg-red-200 dark:hover:bg-red-800 rounded-full p-0.5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Board content */}
        <div className="flex-1 overflow-x-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : !filteredBoardData?.sprint ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-lg font-medium">{selectedProjectId ? 'No active sprint found for this project' : 'No active sprints found'}</p>
              <p className="text-sm mt-1">Go to Backlog to create and manage sprints.</p>
              <button
                onClick={() => navigate('/taskboard/backlog' + (selectedProjectId ? `?project_id=${selectedProjectId}` : ''))}
                className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm rounded-full hover:bg-indigo-700 transition-colors"
              >
                Go to Backlog
              </button>
            </div>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="flex gap-4" style={{ minWidth: `${(filteredBoardData.columns?.length || 5) * 280}px` }}>
                {filteredBoardData.columns?.map((column: any) => (
                  <div key={column.id} className="flex-1 min-w-[260px] max-w-[320px]">
                    {/* Column header */}
                    <div className="flex items-center justify-between mb-3 px-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {column.name}
                        </h3>
                        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/60 min-w-[20px] h-5 flex items-center justify-center rounded-full px-1.5">
                          {column.requirements?.length || 0}
                        </span>
                      </div>
                    </div>

                    {/* Droppable column */}
                    <Droppable droppableId={column.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`min-h-[200px] rounded-lg p-2 space-y-2 transition-colors border ${
                            snapshot.isDraggingOver
                              ? 'bg-indigo-50/70 dark:bg-indigo-900/15 border-indigo-200 dark:border-indigo-800/40'
                              : 'bg-gray-50/80 dark:bg-gray-900/20 border-transparent'
                          }`}
                        >
                          {column.requirements?.map((req: any, index: number) => (
                            <Draggable key={req.id} draggableId={req.id} index={index}>
                              {(dragProvided, dragSnapshot) => (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  className={`transition-transform ${dragSnapshot.isDragging ? 'rotate-[2deg] scale-[1.02] shadow-lg' : ''}`}
                                >
                                  <TaskBoardCard
                                    requirement={req}
                                    onClick={() => navigate(`/taskboard/requirement/${req.id}`)}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                ))}
              </div>
            </DragDropContext>
          )}
        </div>
      </div>

      {/* Column manager modal */}
      {showColumnManager && boardData?.columns && (
        <WorkflowColumnManager
          columns={boardData.columns}
          onClose={() => setShowColumnManager(false)}
          onUpdate={() => {
            setShowColumnManager(false);
            handleRefresh();
          }}
        />
      )}

      {/* Dependency Blocked Modal */}
      {blockedModal?.visible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setBlockedModal(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-t-lg px-6 py-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m-4.343-5.657a8 8 0 1112.686 0L12 21l-5.657-5.657z" />
                </svg>
                <h3 className="text-lg font-semibold text-white">Blocked by Dependencies</h3>
              </div>
              <p className="text-sm text-red-100 mt-1">Predecessor tasks must be completed first</p>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">The following tasks must be completed before this card can be moved:</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {blockedModal.unmetDependencies.map(dep => (
                  <div key={dep.id} className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        {dep.code && <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{dep.code}</span>}
                        <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 capitalize">{dep.status}</span>
                      </div>
                      <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{dep.title}</p>
                    </div>
                    <button
                      onClick={() => { setBlockedModal(null); navigate(`/taskboard/requirement/${dep.id}`); }}
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex-shrink-0"
                    >
                      View
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setBlockedModal(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
              {blockedModal.canOverride && (
                <button
                  onClick={handleForceOverride}
                  disabled={overriding}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {overriding ? 'Moving...' : 'Move Anyway'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default TaskBoard;
